import { useAtom, useAtomValue, useSetAtom } from "jotai";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";
import TextInput from "components/TextInput";

import { clientConfigAtom } from "atoms/clientConfig";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { vinPhotoAtom, vinUploadedAtom } from "atoms/vin/vin";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { Checkpoint } from "types/Checkpoint";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

import { VinGuide, Image as VinImage } from "./VinGuide";
import { useVinForm } from "./hooks/useVinForm";
import { vinPageStyles } from "./styles";

export const Vin = () => {
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);

  const { classes } = vinPageStyles();
  const { t } = useClientTranslation();

  const DEFAULT_IMAGES: VinImage[] = [
    {
      id: "Car",
      alt: t("vin.car"),
      src: "/assets/jpg/vin/car.jpg",
    },
    {
      id: "Driver's side dashboard",
      alt: t("vin.dashboard"),
      src: "/assets/jpg/vin/dashboard.jpg",
      title: t("vin.dashboard"),
    },
    {
      id: "Driver's side door jamb",
      alt: t("vin.door_jamb"),
      src: "/assets/jpg/vin/doorjamb.jpg",
      title: t("vin.door_jamb"),
    },
  ];

  const [vinPhoto, setVinPhoto] = useAtom(vinPhotoAtom);
  const isVinPhotoUploaded = useAtomValue(vinUploadedAtom);

  const { handleSubmit, onType, isValid, error, vin } = useVinForm();
  const { vin: vinConfig } = useAtomValue(clientConfigAtom);
  const toggleDrawer = useSetAtom(pageDrawerAtom);

  const { currentProgressStep, onTrackAndNext } = usePageRouting();

  const showVinInput = !!vinConfig?.showVinInput;
  const exampleImages = vinConfig?.customExampleImages ?? DEFAULT_IMAGES;

  const onDrop = async (files: File[]) => {
    const image = await processImageForUpload({
      file: files[0],
      alt: "Uploaded vin image",
      type: Photo.Additional,
    });

    setVinPhoto([image]);
  };

  const VinPhotoSection = () => (
    <>
      <Button typeVariant="link" className={classes.explanationButton} onClick={toggleDrawer}>
        {t("global.view_examples")}
      </Button>
      <PhotoGrid images={vinPhoto} deleteFromAtom={setVinPhoto} />
    </>
  );

  return (
    <PageLayout
      as="form"
      onSubmit={handleSubmit(onTrackAndNext({ name: "VIN" }))}
      currentStep={currentProgressStep}
      title={t("vin.header")}
      dataTestId="vinHeader"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          {!showVinInput && !isVinPhotoUploaded ? (
            <PhotoUpload onDrop={onDrop} multiple={false}>
              <Button typeVariant={isVinPhotoUploaded ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
                {t("global.add_photo")}
              </Button>
            </PhotoUpload>
          ) : (
            <Button disabled={!isValid} fullWidth type="submit">
              {t("global.continue")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex py={16} px={24} gap={16} style={{ height: "100%" }}>
        <Text sizeVariant="md">{t(showVinInput ? "vin.instruction_input" : "vin.instruction")}</Text>
        <ol className={classes.orderedList}>
          {exampleImages.map(
            (image) =>
              image.title && (
                <li key={image.id}>
                  <Text sizeVariant="md">{image.title}</Text>
                </li>
              )
          )}
        </ol>

        {showVinInput ? (
          <TextInput
            aria-label="vin input"
            name="vin"
            value={vin}
            onChange={onType}
            error={error}
            placeholder={t("vin.input_placeholder")}
          />
        ) : (
          isVinPhotoUploaded && <VinPhotoSection />
        )}
        {!isVinPhotoUploaded && <VinGuide images={exampleImages} />}
      </Flex>
      <BottomDrawer>
        <PhotoGrid images={exampleImages} />
      </BottomDrawer>
    </PageLayout>
  );
};
